import { useTranslation } from "providers/i18n"
import {
  Typography,
  CardMedia,
  CardContent,
  Stack,
  Box,
  useTheme,
  Card,
  Chip,
  SxProps,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { LocationIcon } from "components/icons"
import { getDurationDate } from "utils/date"
import {
  getFeatureDetail,
  IEventDetail,
  StatusByDateRange,
} from "utils/feature"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { useMemo } from "react"
import { getIcon } from "utils/icon"
import { ImdfFeature } from "providers/venue/types"

interface IVerticalEventCardProps {
  event: ImdfFeature
  dense?: boolean
  sx?: SxProps
}

export const VerticalEventCard = ({
  event,
  dense = false,
  sx = {},
}: IVerticalEventCardProps) => {
  const { t } = useTranslation()
  const {
    name,
    coverImage,
    venueLogo,
    startDate,
    endDate,
    hasLocation,
    isAd,
    status,
  } = getFeatureDetail(event) as IEventDetail

  const durationText = useMemo(
    () => getDurationDate(startDate, endDate),
    [startDate, endDate]
  )

  const theme = useTheme()
  const { styleOverrides } = theme.components?.[`VerticalEventCard`] || {}
  const {
    root,
    cardContent,
    cardMedia,
    cardTitle,
    iconContainer,
    venueLogoContainer,
    cardContentStack,
    durationContainer,
    duration,
    cardInfoContainer,
    recommendText,
    recommendTextContainer,
    chipContainer,
    highlightTextChip,
    statusTextContainer,
    statusText,
    bottomChipWrapper,
    topChipWrapper,
  } = styleOverrides

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )
  return (
    <Card
      sx={deepmerge(root, sx)}
      className={dense ? "MuiCardContent-dense" : ""}
    >
      <CardMedia
        component="div"
        className="VerticalEventCard-cardMedia"
        sx={cardMedia}
        image={coverImage}
      >
        <Box sx={topChipWrapper}>
          {isAd && (
            <>
              <Box sx={chipContainer}>
                <Chip
                  label={t("Highlight")}
                  size="small"
                  sx={highlightTextChip}
                />
              </Box>

              <Box sx={recommendTextContainer}>
                <Typography variant="caption" component="p" sx={recommendText}>
                  {t("Recommend")}
                </Typography>
              </Box>
            </>
          )}
          {status === StatusByDateRange.Upcoming && !isAd && (
            <Box sx={statusTextContainer}>
              <Typography variant="caption" component="p" sx={statusText}>
                {t("Upcoming")}
              </Typography>
            </Box>
          )}
        </Box>
      </CardMedia>

      <CardContent sx={cardContent} className="VerticalEventCard-cardContent">
        <Stack sx={cardContentStack}>
          {venueLogo && (
            <Box sx={venueLogoContainer}>
              <CardMedia component="img" image={venueLogo} />
            </Box>
          )}
          <Box sx={bottomChipWrapper}>
            {isAd && (
              <>
                <Box sx={chipContainer}>
                  <Chip
                    label={t("Highlight")}
                    size="small"
                    sx={highlightTextChip}
                  />
                </Box>
                <Box sx={recommendTextContainer}>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={recommendText}
                  >
                    {t("Recommend")}
                  </Typography>
                </Box>
              </>
            )}
            {status === StatusByDateRange.Upcoming && (
              <Box sx={statusTextContainer}>
                <Typography variant="caption" component="p" sx={statusText}>
                  {t("Upcoming")}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={durationContainer}>
            <Typography
              className="VerticalEventCard-durationText"
              sx={duration}
              component="p"
              variant="subTitle"
            >
              {durationText}
            </Typography>
          </Box>
          <Typography
            sx={cardTitle}
            variant="subTitle"
            component="div"
            color="text.primary"
            className="VerticalEventCard-cardTitleText"
          >
            {t(name)}
          </Typography>
        </Stack>
        {hasLocation && (
          <Box sx={cardInfoContainer}>
            <Box sx={iconContainer} color="text.secondary">
              {locationIcon}
            </Box>
            <LocationInfo
              feature={event}
              variant="subTitle"
              color="text.secondary"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
